// Единая точка HTTP-запросов к backend. initData из Telegram передаётся в каждом запросе —
// backend валидирует её на каждый вызов (ТЗ раздел 4.5), клиент сам ничего не проверяет и не хранит
// секретов (принцип "единой точки правды", ТЗ раздел 2.2).

import WebApp from "@twa-dev/sdk";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-Telegram-Init-Data": WebApp.initData,
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = await response.text();
    throw new ApiError(response.status, body || response.statusText);
  }

  // 204 No Content (DELETE-эндпоинты) — тела нет, response.json() упал бы на пустой строке.
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  delete: <T = void>(path: string) => request<T>(path, { method: "DELETE" }),
};

// Casing и состав полей — как у backend Pydantic-схем (app/schemas/*.py), snake_case, не
// camelCase: FastAPI/Pydantic здесь ничего не транслитерирует, а вводить отдельный слой
// маппинга ради одной лишь смены регистра букв — накладные расходы без пользы (см. docs/DECISIONS.md).

export interface Plan {
  id: number;
  code: string;
  title: string;
  price: number;
  currency: string;
  duration_days: number;
  device_limit: number;
  is_trial: boolean;
}

export type SubscriptionStatus = "active" | "expiring" | "expired" | "cancelled";

export interface Device {
  id: number;
  name: string;
  os: string | null;
  attached_at: string;
  last_seen_at: string | null;
  is_primary: boolean;
}

export interface Subscription {
  id: number;
  status: SubscriptionStatus;
  plan: Plan;
  started_at: string;
  expires_at: string;
  auto_renew: boolean;
  devices: Device[];
}

export interface CheckoutResponse {
  invoice_link: string;
}

export interface InstallPackage {
  os: string;
  url: string;
}

export interface AttachDeviceResponse {
  device: Device;
  install_packages: InstallPackage[];
}

export const fetchPlans = () => api.get<Plan[]>("/miniapp/plans");
export const fetchSubscription = () => api.get<Subscription | null>("/miniapp/subscription");
export const checkout = (planId: number) =>
  api.post<CheckoutResponse>("/miniapp/checkout", { plan_id: planId });
export const attachDevice = (name: string) =>
  api.post<AttachDeviceResponse>("/miniapp/devices", { name });
export const fetchDeviceInstallPackages = (deviceId: number) =>
  api.get<InstallPackage[]>(`/miniapp/devices/${deviceId}/install-packages`);
export const detachDevice = (deviceId: number) => api.delete(`/miniapp/devices/${deviceId}`);
